import type { VisualEditorCommand } from '../editor-types';
import { getDataLikeRow, getNearestCell, isDataLikeTable, placeCaretInCell } from './tables';

export const TABLE_ROW_CHROME_HOST_ATTRIBUTE = 'data-html-editor-table-chrome';

export interface TableRowChromeLabels {
    menu: string;
    addRowAbove: string;
    addRowBelow: string;
    deleteRow: string;
    addColumnBefore: string;
    addColumnAfter: string;
    deleteColumn: string;
    deleteTable: string;
    rowBorder: string;
    clearRowBorder: string;
}

export type TableRowChromeAction = Extract<
    VisualEditorCommand,
    | { type: 'addRow'; position: 'above' | 'below' }
    | { type: 'deleteRow' }
    | { type: 'addColumn'; position: 'before' | 'after'; columnIndex?: number }
    | { type: 'deleteColumn'; columnIndex?: number }
    | { type: 'deleteTable' }
    | { type: 'rowBorderBottom'; border: 'none' | 'light' }
>;

const CHROME_STYLE = `
[${TABLE_ROW_CHROME_HOST_ATTRIBUTE}] {
  position: absolute;
  z-index: 20;
  display: none;
  pointer-events: none;
}
[${TABLE_ROW_CHROME_HOST_ATTRIBUTE}][data-visible="true"] {
  display: block;
  pointer-events: auto;
}
[${TABLE_ROW_CHROME_HOST_ATTRIBUTE}] button {
  pointer-events: auto;
  border: 1px solid #d0d5dd;
  background: #fff;
  border-radius: 8px;
  height: 28px;
  min-width: 28px;
  padding: 0 6px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: #344054;
}
[${TABLE_ROW_CHROME_HOST_ATTRIBUTE}] [data-role="menu"] {
  display: none;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
  padding: 4px;
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(16, 24, 40, 0.12);
  min-width: 160px;
}
[${TABLE_ROW_CHROME_HOST_ATTRIBUTE}][data-open="true"] [data-role="menu"] {
  display: flex;
}
[${TABLE_ROW_CHROME_HOST_ATTRIBUTE}] [data-role="menu"] button {
  width: 100%;
  height: 32px;
  text-align: left;
  padding: 0 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
}
[${TABLE_ROW_CHROME_HOST_ATTRIBUTE}] [data-role="menu"] button:hover {
  background: #f2f4f7;
}
`;

const ACTIONS: Array<{ command: TableRowChromeAction; labelKey: keyof TableRowChromeLabels }> = [
    { command: { type: 'addRow', position: 'above' }, labelKey: 'addRowAbove' },
    { command: { type: 'addRow', position: 'below' }, labelKey: 'addRowBelow' },
    { command: { type: 'deleteRow' }, labelKey: 'deleteRow' },
    { command: { type: 'addColumn', position: 'before' }, labelKey: 'addColumnBefore' },
    { command: { type: 'addColumn', position: 'after' }, labelKey: 'addColumnAfter' },
    { command: { type: 'deleteColumn' }, labelKey: 'deleteColumn' },
    { command: { type: 'rowBorderBottom', border: 'light' }, labelKey: 'rowBorder' },
    { command: { type: 'rowBorderBottom', border: 'none' }, labelKey: 'clearRowBorder' },
    { command: { type: 'deleteTable' }, labelKey: 'deleteTable' }
];

export const selectTableRowForEdit = (document: Document, row: HTMLTableRowElement): Range | null => {
    const selection = document.getSelection();
    const current = getNearestCell(selection?.anchorNode ?? null);
    const columnIndex = current && row.contains(current) ? current.cellIndex : 0;
    const cell = row.cells[columnIndex] ?? row.cells[0] ?? getNearestCell(row);
    placeCaretInCell(document, cell);
    if (!cell) return null;
    const range = document.createRange();
    range.setStart(cell, 0);
    range.collapse(true);
    return range;
};

export const attachTableRowChrome = (
    document: Document,
    labels: TableRowChromeLabels,
    onCommand: (_command: TableRowChromeAction, _range: Range | null) => void
): (() => void) => {
    const style = document.createElement('style');
    style.setAttribute(TABLE_ROW_CHROME_HOST_ATTRIBUTE, 'true');
    style.textContent = CHROME_STYLE;
    document.head.appendChild(style);

    const host = document.createElement('div');
    host.setAttribute(TABLE_ROW_CHROME_HOST_ATTRIBUTE, 'true');
    host.setAttribute('contenteditable', 'false');
    host.setAttribute('data-visible', 'false');
    host.setAttribute('data-open', 'false');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.setAttribute('aria-label', labels.menu);
    trigger.setAttribute('title', labels.menu);
    trigger.textContent = '⋮';
    host.appendChild(trigger);

    const menu = document.createElement('div');
    menu.setAttribute('data-role', 'menu');
    menu.setAttribute('role', 'menu');
    menu.setAttribute('aria-label', labels.menu);

    ACTIONS.forEach(({ command, labelKey }) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.setAttribute('role', 'menuitem');
        item.setAttribute('aria-label', labels[labelKey]);
        item.textContent = labels[labelKey];
        item.addEventListener('mousedown', (event) => event.preventDefault());
        item.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const row = activeRow;
            if (!row?.isConnected) return;
            const selection = document.getSelection();
            const beforeCell = getNearestCell(selection?.anchorNode ?? null);
            const columnIndex = beforeCell && row.contains(beforeCell) ? beforeCell.cellIndex : 0;
            const range = selectTableRowForEdit(document, row);
            const nextCommand: TableRowChromeAction =
                command.type === 'deleteColumn'
                    ? { type: 'deleteColumn', columnIndex }
                    : command.type === 'addColumn'
                      ? { type: 'addColumn', position: command.position, columnIndex }
                      : command;
            onCommand(nextCommand, range);
            closeMenu();
            hide();
        });
        menu.appendChild(item);
    });
    host.appendChild(menu);
    document.body.appendChild(host);

    let activeRow: HTMLTableRowElement | null = null;

    const closeMenu = () => host.setAttribute('data-open', 'false');
    const hide = () => {
        host.setAttribute('data-visible', 'false');
        closeMenu();
        activeRow = null;
    };

    const positionOver = (row: HTMLTableRowElement) => {
        activeRow = row;
        const rect = row.getBoundingClientRect();
        const top = rect.top + (document.defaultView?.scrollY ?? 0);
        const left = Math.max(4, rect.left + (document.defaultView?.scrollX ?? 0) - 36);
        host.style.top = `${top}px`;
        host.style.left = `${left}px`;
        host.setAttribute('data-visible', 'true');
    };

    /** Keep chrome reachable: host sits left of the row, so the pointer leaves the row before entering the host. */
    const isPointerInChromeBridge = (event: MouseEvent): boolean => {
        if (!activeRow?.isConnected) return false;
        const rowRect = activeRow.getBoundingClientRect();
        const hostRect = host.getBoundingClientRect();
        const pad = 6;
        const left = Math.min(hostRect.left, rowRect.left) - pad;
        const right = rowRect.left + pad;
        const top = Math.min(hostRect.top, rowRect.top) - pad;
        const bottom = Math.max(hostRect.bottom, rowRect.bottom) + pad;
        return event.clientX >= left && event.clientX <= right && event.clientY >= top && event.clientY <= bottom;
    };

    const handleMouseMove = (event: MouseEvent) => {
        const target = event.target as Node | null;
        if (host.contains(target)) return;

        const row = getDataLikeRow(target);
        if (!row) {
            const keepForBridge = isPointerInChromeBridge(event);
            if (keepForBridge || host.getAttribute('data-open') === 'true') return;
            hide();
            return;
        }
        positionOver(row);
    };

    const handleMouseLeave = () => {
        if (host.getAttribute('data-open') === 'true') return;
        hide();
    };

    trigger.addEventListener('mousedown', (event) => event.preventDefault());
    trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const open = host.getAttribute('data-open') === 'true';
        host.setAttribute('data-open', open ? 'false' : 'true');
    });

    document.addEventListener('mousemove', handleMouseMove);
    document.documentElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
        host.remove();
        style.remove();
    };
};

export { isDataLikeTable };
